import * as React from 'react'

import { cn } from '../../utils/cn'

interface FileUploadProps {
  /** The selected file(s) */
  value?: File | File[] | null
  /** Callback when file(s) are selected */
  onChange?: (files: File | File[] | null) => void
  /** Accept specific file types (e.g., ".csv,.pdf" or "image/*") */
  accept?: string
  /** Allow multiple file selection */
  multiple?: boolean
  /** Optional className for the wrapper */
  className?: string
  /** Child elements (typically a button or custom trigger) */
  children?: React.ReactNode
  /** Disable the file input */
  disabled?: boolean
}

/**
 * FileUpload component with hidden input and custom trigger.
 *
 * @example
 * ```tsx
 * const [file, setFile] = useState<File | null>(null)
 *
 * <FileUpload onChange={setFile} accept=".csv">
 *   <Button>Upload CSV</Button>
 * </FileUpload>
 * ```
 *
 * @example Multiple files
 * ```tsx
 * const [files, setFiles] = useState<File[]>([])
 *
 * <FileUpload onChange={setFiles} multiple>
 *   <Button>Upload Files</Button>
 * </FileUpload>
 * ```
 */
function FileUpload({
  onChange,
  accept,
  multiple = false,
  className,
  children,
  disabled = false,
}: FileUploadProps) {
  const inputRef = React.useRef<HTMLInputElement>(null)

  const handleClick = () => {
    if (!disabled) {
      inputRef.current?.click()
    }
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files

    if (!files || files.length === 0) {
      onChange?.(null)
      return
    }

    if (multiple) {
      onChange?.(Array.from(files))
    } else {
      onChange?.(files[0])
    }
  }

  // Handle keyboard accessibility
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if ((event.key === 'Enter' || event.key === ' ') && !disabled) {
      event.preventDefault()
      inputRef.current?.click()
    }
  }

  return (
    <div
      className={cn('inline-block', className)}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={handleChange}
        disabled={disabled}
        className="hidden"
        aria-hidden="true"
      />
      {children}
    </div>
  )
}

export { FileUpload }
export type { FileUploadProps }
